import type { ComponentProps } from 'react'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import styled from 'styled-components'
import useTourV2VariationDetails from 'hooks/TourV2/useTourV2VariationDetails'
import Modal from 'LuxKit/Modal/Modal'
import TourV2ItemInclusions from 'components/OfferPage/TourV2/TourV2ItemInclusions'
import Group from 'components/utils/Group'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { partitionBy } from 'lib/array/arrayUtils'
import TourV2HighlightedInclusionItem from 'components/OfferPage/TourV2/TourV2HighlightedInclusionItem'
import LabeledSheet from 'LuxKit/LabeledSheet'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { connect } from 'react-redux'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

const CaptionContainer = styled(Group)`
  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const HighlightedInclusionsContainer = styled(Group)`
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

type InheritedModalProps = Pick<ComponentProps<typeof Modal>, 'primaryActionText' | 'primaryActionTo'>

interface MappedStateProps {
  isLuxPlusEnabled: boolean
}
interface Props extends InheritedModalProps, MappedStateProps {
  variation: App.Tours.TourV2OfferVariation
}

export type TourV2AllInclusionsModalResult = 'primary-action' | undefined

function TourV2AllInclusionsModal({
  variation,
  primaryActionText,
  primaryActionTo,
  isLuxPlusEnabled,
}: Props) {
  const modalElement = useModalElementContext<TourV2AllInclusionsModalResult>()
  const intl = useIntl()

  const handlePrimaryClick = useCallback(() => {
    modalElement.resolve('primary-action')
  }, [modalElement])

  const {
    durationInDaysCount,
    countriesCount,
    placesCount,
    hasTheSameStartAndEndLocation,
    startLocation,
    endLocation,
  } = useTourV2VariationDetails(variation)

  const [flashInclusionItems, nonFlashInclusionItems] = useMemo(() => partitionBy(variation.inclusionItems, inclusion => !!inclusion.isFlash), [variation.inclusionItems])
  const nonFlashFilteredInclusions = useMemo(() => {
    if (isLuxPlusEnabled) {
      return nonFlashInclusionItems
    }
    return nonFlashInclusionItems.filter(inclusion => !inclusion.isLuxPlusBonus)
  }, [nonFlashInclusionItems, isLuxPlusEnabled])
  const [bonusInclusionItems, inclusionItems] = useMemo(() => partitionBy(nonFlashFilteredInclusions, inclusion => !!inclusion.isLuxPlusBonus), [nonFlashFilteredInclusions])
  inclusionItems.sort(inclusion => inclusion.isHighlighted ? -1 : 1)
  return <Modal
    title={variation.name}
    subtitle={<CaptionContainer direction="horizontal" verticalAlign="center" wrap="wrap">
      <BodyText variant="medium" colour="light-foreground-alt">
        {hasTheSameStartAndEndLocation && <span>
          <FormattedMessage
            id="xr0BzZg4kV2_2Qda7jDD"
            defaultMessage="Starts and ends in <bold>{endLocation}</bold>"
            
            values={{
              endLocation,
              bold: (chunks) => <b>{chunks}</b>,
            }}
          />
        </span>}
        {!hasTheSameStartAndEndLocation && <span>
          <FormattedMessage
            id="u9-n38mJTmtIcyHDjU_Z"
            defaultMessage="Starts in <bold>{startLocation}</bold>. Ends in <bold>{endLocation}</bold>"
            
            values={{
              startLocation,
              endLocation,
              bold: (chunks) => <b>{chunks}</b>,
            }}
          />
        </span>}
      </BodyText>
      <BodyText variant="medium">{durationInDaysCount}</BodyText>
      {placesCount && <BodyText variant="medium">{placesCount}</BodyText>}
      {countriesCount && <BodyText variant="medium">{countriesCount}</BodyText>}
    </CaptionContainer>}
    primaryActionText={primaryActionText}
    primaryActionTo={primaryActionTo}
    onPrimaryActionClick={!primaryActionTo ? handlePrimaryClick : undefined}
  >
    <Group direction="vertical" gap={8}>
      {flashInclusionItems.length === 0 && bonusInclusionItems.length === 0 && <BodyText weight="bold" variant="large">{intl.formatMessage({
        id: '-bGFPQuryBkkcABlqDYC',
        defaultMessage: 'Highlights included',
      })}
      </BodyText>}
      {!!bonusInclusionItems.length && <LabeledSheet kind="lux-plus" label={<LuxPlusLabel type="bonus-inclusions" />}>
        <HighlightedInclusionsContainer direction="vertical" gap={12}>
          <Group direction="vertical" gap={4}>
            {bonusInclusionItems.map((inclusion) => <TourV2HighlightedInclusionItem
              key={inclusion.id}
              inclusion={inclusion}
            />)}
          </Group>
        </HighlightedInclusionsContainer>
      </LabeledSheet>}
      {!!flashInclusionItems.length && !bonusInclusionItems.length && <LabeledSheet
        kind="exclusive"
        label={intl.formatMessage({
          id: 'FqIRSx1qTsfxrmGb2I5r',
          defaultMessage: 'Limited time inclusions',
        })}
      >
        <HighlightedInclusionsContainer direction="vertical" gap={12}>
          <Group direction="vertical" gap={4}>
            {flashInclusionItems.map((inclusion) => <TourV2HighlightedInclusionItem
              key={inclusion.id}
              inclusion={inclusion}
            />)}
          </Group>
        </HighlightedInclusionsContainer>
      </LabeledSheet>}
      {!!flashInclusionItems.length && !!bonusInclusionItems.length && <TourV2ItemInclusions inclusions={flashInclusionItems} gap={4} />}
      <Group direction="vertical" gap={4} noDisplayWhenEmpty>
        {inclusionItems && <TourV2ItemInclusions showHighlight={false} inclusions={inclusionItems} gap={4} />}
        <LoyaltyInclusions variation={variation} variant="offer-page" />
      </Group>
    </Group>
  </Modal>
}

export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    isLuxPlusEnabled: getIsLuxPlusToursEnabled(state),
  }
})(TourV2AllInclusionsModal)
